import { mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import { enterMovesToNextLine } from "./line-navigation"
import { clampTileColumns, DEFAULT_TILE_COLUMNS } from "./tile-html"

// Above the paragraph's own `p` rule, which would claim every line.
const PARSE_PRIORITY = 1000

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		tiles: {
			insertTiles: (count?: number) => ReturnType
			setTileColumns: (pos: number, cols: number) => ReturnType
		}
	}
}

/** The patterned box above the text; Mintlify's tile children. */
export const TilePreviewExtension = Node.create({
	name: "tilePreview",
	content: "block+",
	defining: true,

	parseHTML() {
		return [{ tag: "div[data-tile-preview]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-tile-preview": "",
				class: "pl-tile-preview",
			}),
			0,
		]
	},
})

/**
 * Like the card's title, a link on this line is the tile's `href`, and the
 * portal stretches it over the whole tile.
 */
export const TileTitleExtension = Node.create({
	name: "tileTitle",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-tile-title]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-tile-title": "",
				class: "pl-tile-title",
			}),
			0,
		]
	},
})

export const TileDescriptionExtension = Node.create({
	name: "tileDescription",
	content: "text*",
	marks: "",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-tile-description]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-tile-description": "",
				class: "pl-tile-description",
			}),
			0,
		]
	},
})

export const TileExtension = Node.create({
	name: "tile",
	// Its keys run before the core Enter chain, which tries to split the line.
	priority: 200,
	content: "tilePreview tileTitle tileDescription",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="tile"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tile",
				class: "pl-tile",
			}),
			0,
		]
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => enterMovesToNextLine(this.editor, ["tileTitle"]),
		}
	},
})

export const TileGroupExtension = Node.create({
	name: "tileGroup",
	group: "block",
	content: "tile+",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			cols: {
				default: DEFAULT_TILE_COLUMNS,
				parseHTML: (element: HTMLElement) =>
					clampTileColumns(element.getAttribute("data-cols")),
				renderHTML: (attributes: Record<string, unknown>) => {
					const cols = clampTileColumns(attributes.cols)
					return { "data-cols": String(cols), style: `--cols: ${cols}` }
				},
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="tile-group"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tile-group",
				class: "pl-tile-group",
			}),
			0,
		]
	},

	addCommands() {
		return {
			insertTiles:
				(count = DEFAULT_TILE_COLUMNS) =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							attrs: { cols: clampTileColumns(count) },
							content: Array.from({ length: Math.max(1, count) }, () => ({
								type: "tile",
								content: [
									{ type: "tilePreview", content: [{ type: "paragraph" }] },
									{ type: "tileTitle" },
									{ type: "tileDescription" },
								],
							})),
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === "tileTitle" && pos >= from - 1) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target + 1)))
							}
							return true
						})
						.run()
				},

			setTileColumns:
				(pos, cols) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== this.name) return false
					if (dispatch) {
						tr.setNodeMarkup(pos, undefined, {
							...node.attrs,
							cols: clampTileColumns(cols),
						})
					}
					return true
				},
		}
	},
})

export const TileExtensions = [
	TileGroupExtension,
	TileExtension,
	TilePreviewExtension,
	TileTitleExtension,
	TileDescriptionExtension,
]
